import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { MaskLines } from "@/components/site/Reveal";
import { QuoteButton, CallButton } from "@/components/site/Cta";
import { CERTS } from "@/content/site";

const EASE = [0.16, 1, 0.3, 1];
const fade = (d) => ({ initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { duration: 1, delay: d, ease: EASE } });

const SolarArray = ({ rx, ry }) => (
  <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative mx-auto w-[150%] sm:w-[120%] lg:w-full [transform-style:preserve-3d]">
    <div className="grid grid-cols-10 gap-[6px] sm:gap-2 p-2">
      {Array.from({ length: 50 }).map((_, i) => (
        <div key={i} className="relative aspect-[1/1.45] rounded-[3px] border border-white/20 bg-gradient-to-br from-[#2a3242] via-[#121722] to-[#07090c] overflow-hidden shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]">
          <div className="absolute inset-0 opacity-60 [background-image:linear-gradient(rgba(209,213,219,0.12)_1px,transparent_1px),linear-gradient(90deg,rgba(209,213,219,0.12)_1px,transparent_1px)] [background-size:33.3%_16.6%]" />
        </div>
      ))}
    </div>
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="light-sweep absolute -inset-y-10 left-0 w-1/3 bg-gradient-to-r from-transparent via-amber-200/35 to-transparent blur-md" />
    </div>
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const ry = useSpring(useTransform(mx, [-1, 1], [-7, 7]), { stiffness: 60, damping: 18 });
  const rxBase = useSpring(useTransform(my, [-1, 1], [64, 56]), { stiffness: 60, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const arrayY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const sunY = useTransform(scrollYProgress, [0, 1], ["0%", "45%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width) * 2 - 1);
    my.set(((e.clientY - r.top) / r.height) * 2 - 1);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section" className="grain relative isolate min-h-[100svh] overflow-hidden bg-[#08090a] pt-[72px]">
      <motion.div style={{ y: sunY }} className="pointer-events-none absolute inset-0 -z-10">
        <div className="sun-breathe absolute right-[-20%] top-[30%] h-[70vw] w-[70vw] max-h-[900px] max-w-[900px] rounded-full bg-[radial-gradient(circle,rgba(255,196,94,0.55)_0%,rgba(245,166,35,0.22)_35%,transparent_68%)] lg:right-[-6%] lg:top-[12%]" />
        <svg viewBox="0 0 600 600" className="spin-slow absolute right-[-30%] top-[18%] h-[95vw] w-[95vw] max-h-[1100px] max-w-[1100px] opacity-40 lg:right-[-14%] lg:top-[-6%]" aria-hidden="true">
          <circle cx="300" cy="300" r="290" fill="none" stroke="rgba(209,213,219,0.18)" strokeDasharray="2 10" />
          <circle cx="300" cy="300" r="230" fill="none" stroke="rgba(245,166,35,0.25)" />
          <circle cx="300" cy="300" r="170" fill="none" stroke="rgba(209,213,219,0.12)" strokeDasharray="1 6" />
        </svg>
      </motion.div>

      <motion.div style={{ y: arrayY }} className="pointer-events-none absolute inset-x-0 bottom-[-18%] -z-10 [perspective:1100px] lg:left-[38%] lg:right-[-6%] lg:bottom-[-10%]">
        <motion.div initial={{ opacity: 0, y: 80 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.8, delay: 0.3, ease: EASE }}>
          <SolarArray rx={rxBase} ry={ry} />
        </motion.div>
      </motion.div>
      <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-r from-[#08090a] via-[#08090a]/85 to-transparent lg:via-[#08090a]/60" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-1/2 bg-gradient-to-t from-[#08090a] via-[#08090a]/70 to-transparent" />

      <motion.div style={{ y: contentY }} className="relative mx-auto flex min-h-[calc(100svh-72px)] max-w-[1440px] flex-col justify-center px-5 sm:px-8 lg:px-12 py-16 lg:py-20">
        <motion.div {...fade(0.1)} className="flex items-center gap-3">
          <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-amber-400" /></span>
          <span className="eyebrow">Texas Solar · Battery · Electrical</span>
        </motion.div>

        <MaskLines as="h1" onLoad delay={0.2} lines={["Power your home", <span key="c" className="amber-text">with confidence.</span>]}
          className="mt-6 max-w-4xl text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold uppercase leading-[1.04] text-white" />

        <motion.p {...fade(0.75)} className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-zinc-200">
          Premium solar, battery, and electrical solutions engineered for Texas homeowners and businesses.
        </motion.p>
        <motion.p {...fade(0.85)} className="mt-3 max-w-xl text-sm leading-relaxed text-zinc-400">
          Solar Energy Doc LLC is a licensed and insured Texas solar company delivering dependable solar installation, maintenance, troubleshooting, battery storage, electrical work, and more.
        </motion.p>

        <motion.div {...fade(0.95)} className="mt-9 flex flex-col gap-3 sm:flex-row">
          <QuoteButton testId="hero-cta-quote">Get Your Free Solar Quote</QuoteButton>
          <CallButton testId="hero-cta-phone">Talk to a Solar Expert</CallButton>
        </motion.div>

        <motion.ul {...fade(1.1)} data-testid="hero-trust-badges" className="mt-10 grid max-w-3xl grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-2.5">
          {CERTS.map((c) => (
            <li key={c.key} className="glass flex items-center gap-2 rounded-full px-3.5 py-2">
              <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-amber-400" />
              <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.1em] text-zinc-100">{c.title}</span>
            </li>
          ))}
        </motion.ul>

        <motion.p {...fade(1.25)} className="mt-8 font-mono text-[11px] sm:text-xs uppercase tracking-[0.22em] text-zinc-400">
          Quality You Can Trust. <span className="text-amber-400/90">Service Built for Texas.</span>
        </motion.p>
      </motion.div>
    </section>
  );
};
